export const actionTypes = {
  SendMessage: 'sendMessage',
  AddPatientTag: 'addPatientTag',
  ShowAnnouncement: 'showAnnouncement',
  AssignPatient: 'assignPatient',
  NotifyPractice: 'notifyPractice'
}

// Mark as Needs Attention raises the HI (premium) flow on the practice, so only a practice whose
// plan carries HI may create one. Two deliberate exceptions:
//
// - A system rule is authored by Grin and is not scoped to any one practice's plan, so the plan
//   says nothing about whether the action is appropriate. The ops editor also has no practice
//   context to read a plan from, so gating it on the plan would hide the action there entirely.
// - A rule that is already saved with the action keeps offering it. SelectInput falls back to
//   rendering the raw value when it is out of range, so removing it would show the untranslated
//   `notifyPractice` slug with no way to re-select it. Pass the SAVED action type here, never the
//   live dropdown state: keying on live state means switching away once removes the option and
//   traps the user, which is the removal we were asked to avoid.
export const canSelectNotifyPractice = ({ isHiPlan, isSystemRule, savedActionType } = {}) =>
  Boolean(isHiPlan) || Boolean(isSystemRule) || savedActionType === actionTypes.NotifyPractice

export const conditionTypes = {
  DateRange: 'dateRange',
  DuringDays: 'duringDays',
  AlignerNumber: 'alignerNumber',
  AlignerNumberEvery: 'alignerNumberEvery',
  TreatmentStatusUpdated: 'treatmentStatusUpdated',
  PatientScanCount: 'patientScanCount',
  PatientScanCountEvery: 'patientScanCountEvery',
  PracticeHasFeatureFlag: 'practiceHasFeatureFlag',
  PracticeDoesNotHaveFeatureFlag: 'practiceDoesNotHaveFeatureFlag',
  ScanIsUrgent: 'scanIsUrgent',
  OverdueDays: 'overdueDays',
  RecurringWeeks: 'recurringWeeks',
  PracticeOnPlan: 'practiceOnPlan',
  PatientStatus: 'patientStatus',
  RemainingAligners: 'remainingAligners',
  PatientHasTags: 'patientHasTags'
}

export const TriggerTypes = {
  IncomingMessage: 'incomingMessage',
  IncomingMessageOrGrinScan: 'incomingMessageOrGrinScan',
  IncomingGrinScan: 'incomingGrinScan',
  AlignerNumberReached: 'alignerNumberReached',
  AlignerNumberEveryReached: 'alignerNumberEveryReached',
  ScanNumberReached: 'scanNumberReached',
  PatientStatusChanged: 'patientStatusChanged',
  PatientOverdueForXDays: 'patientOverdueForXDays',
  PatientEveryXWeeks: 'patientEveryXWeeks',
  RemainingAlignersReached: 'remainingAlignersReached',
  LeadCreated: 'leadCreated'
}

export const TriggerConfigs = {
  [TriggerTypes.IncomingMessage]: {
    isRequired: false,
    conditions: [
      conditionTypes.DateRange,
      conditionTypes.DuringDays,
      conditionTypes.PatientStatus,
      conditionTypes.PatientHasTags,
      conditionTypes.PracticeOnPlan
    ]
  },
  [TriggerTypes.IncomingMessageOrGrinScan]: {
    isRequired: false,
    conditions: [
      conditionTypes.DateRange,
      conditionTypes.DuringDays,
      conditionTypes.PracticeHasFeatureFlag,
      conditionTypes.PracticeDoesNotHaveFeatureFlag,
      conditionTypes.PatientStatus,
      conditionTypes.PatientHasTags,
      conditionTypes.PracticeOnPlan
    ]
  },
  [TriggerTypes.IncomingGrinScan]: {
    isRequired: false,
    conditions: [
      conditionTypes.DateRange,
      conditionTypes.DuringDays,
      conditionTypes.PatientScanCount,
      conditionTypes.PatientScanCountEvery,
      conditionTypes.PracticeHasFeatureFlag,
      conditionTypes.ScanIsUrgent,
      conditionTypes.PracticeOnPlan,
      conditionTypes.PatientStatus,
      conditionTypes.PatientHasTags
    ]
  },
  [TriggerTypes.AlignerNumberReached]: {
    isRequired: true,
    conditions: [conditionTypes.AlignerNumber]
  },
  [TriggerTypes.AlignerNumberEveryReached]: {
    isRequired: true,
    conditions: [conditionTypes.AlignerNumberEvery, conditionTypes.PatientStatus, conditionTypes.PatientHasTags],
    requiredConditions: [conditionTypes.AlignerNumberEvery]
  },
  // GR-10710. Reuses the patientScanCount condition, which stays ops-only as an optional condition
  // on the incoming-scan trigger; required here, so the editor offers it to practices for this trigger.
  [TriggerTypes.ScanNumberReached]: {
    isRequired: true,
    conditions: [conditionTypes.PatientScanCount, conditionTypes.PatientStatus, conditionTypes.PatientHasTags],
    requiredConditions: [conditionTypes.PatientScanCount]
  },
  [TriggerTypes.RemainingAlignersReached]: {
    isRequired: true,
    conditions: [conditionTypes.RemainingAligners]
  },
  [TriggerTypes.PatientStatusChanged]: {
    isRequired: true,
    conditions: [conditionTypes.TreatmentStatusUpdated]
  },
  [TriggerTypes.PatientOverdueForXDays]: {
    isRequired: true,
    conditions: [conditionTypes.OverdueDays, conditionTypes.PatientStatus, conditionTypes.PatientHasTags],
    requiredConditions: [conditionTypes.OverdueDays],
    isTimeBased: true
  },
  [TriggerTypes.PatientEveryXWeeks]: {
    isRequired: true,
    conditions: [conditionTypes.RecurringWeeks, conditionTypes.PatientStatus, conditionTypes.PatientHasTags],
    requiredConditions: [conditionTypes.RecurringWeeks],
    isTimeBased: true
  },
  [TriggerTypes.LeadCreated]: {
    isRequired: false,
    conditions: [
      conditionTypes.DateRange,
      conditionTypes.DuringDays,
      conditionTypes.PracticeHasFeatureFlag,
      conditionTypes.PracticeOnPlan,
      conditionTypes.PatientStatus,
      conditionTypes.PatientHasTags
    ]
  }
}

export const SystemAutomationsDoctorId = 'Grin'

export const MIN_OVERDUE_DAYS = 1
export const MAX_OVERDUE_DAYS = 999

// The hour a time-based rule runs at when no action carries one yet. A new rule gets the
// friendlier 10:00. An existing rule with no stored hour has been running at midnight (the
// backend's fallback), so it gets '00:00': that shows the real value, and saving it without a
// deliberate change stays a no-op instead of a silent reschedule. Do not "simplify" it to 10:00,
// that moves every legacy rule on its next save.
// Read through getDefaultExecutionHour by the hour picker (AutomationRulesActionEditor ->
// RuleExecutionHourInput) and through getRuleExecutionHour by the hint under the trigger
// (AutomationRuleEditorModal -> RuleEditor), so the sentence cannot drift from the picker.
export const NEW_RULE_DEFAULT_EXECUTION_HOUR = '10:00'
export const EXISTING_RULE_DEFAULT_EXECUTION_HOUR = '00:00'

export const getDefaultExecutionHour = ({ isNewRule }) =>
  isNewRule ? NEW_RULE_DEFAULT_EXECUTION_HOUR : EXISTING_RULE_DEFAULT_EXECUTION_HOUR

// The hour the rule will run at: the one the picker wrote to an action, otherwise the default the
// picker would seed - which is what the rule would run at if saved untouched.
export const getRuleExecutionHour = ({ actions, isNewRule }) =>
  actions?.find(action => action.params?.executionHour)?.params?.executionHour || getDefaultExecutionHour({ isNewRule })

// One week is the shortest interval "every X weeks" can describe. The upper bound is a year:
// beyond that the rule fires at most once, which a specific-date reminder expresses better.
export const MIN_RECURRING_WEEKS = 1
export const MAX_RECURRING_WEEKS = 52

// v1 anchors recurrence to when the patient joined the platform. Stored on the condition so a
// practice-selectable anchor can be added without a second trigger.
export const RECURRENCE_ANCHOR_PATIENT_CREATION = 'patientCreation'

// An interval below 1 cannot describe "every Nth", and the backend rejects it outright, so the
// UI must stop a rule saving into a permanently dead state.
export const MIN_SCANS_INTERVAL = 1
export const MIN_ALIGNER_INTERVAL = 1
export const MIN_SCANS_COUNT = 1
